import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/lib/auth';
import LoginPage from '@/pages/Login';
const ResetPasswordPage = React.lazy(() => import('@/pages/ResetPassword'));
const LeadsPage = React.lazy(() => import('@/pages/Leads'));
const DashboardPage = React.lazy(() => import('@/pages/Dashboard'));
const ImportPage = React.lazy(() => import('@/pages/Import'));
const IntegrationsPage = React.lazy(() => import('@/pages/Integrations'));
const SettingsPage = React.lazy(() => import('@/pages/Settings'));
const CampaignsPage = React.lazy(() => import('@/pages/Campaigns'));
const AutomationPage = React.lazy(() => import('@/pages/Automation'));
const InboxPage = React.lazy(() => import('@/pages/Inbox'));
const TeamPage = React.lazy(() => import('@/pages/Team'));
const PerformancePage = React.lazy(() => import('@/pages/Performance'));
const AuditLogPage = React.lazy(() => import('@/pages/AuditLog'));
const AdsPage = React.lazy(() => import('@/pages/Ads'));
const ForecastPage = React.lazy(() => import('@/pages/Forecast'));
const DuplicatesPage = React.lazy(() => import('@/pages/Duplicates'));
const PrivacyPolicyPage = React.lazy(() => import('@/pages/PrivacyPolicyPage'));
const PrivacyPolicyPageBM = React.lazy(() => import('@/pages/PrivacyPolicyPageBM'));
const TermsPage = React.lazy(() => import('@/pages/TermsPage'));
const TermsOfServicePageBM = React.lazy(() => import('@/pages/TermsOfServicePageBM'));
const DataDeletionPage = React.lazy(() => import('@/pages/DataDeletionPage'));
const DataDeletionPageBM = React.lazy(() => import('@/pages/DataDeletionPageBM'));
const DeletionStatusPage = React.lazy(() => import('@/pages/DeletionStatusPage'));
import HomePage from '@/pages/HomePage';
import AppShell from '@/components/AppShell';

const PageLoading = () => <div className="flex-1 grid place-items-center min-h-[50vh]" data-testid="page-loading"><span className="h-6 w-6 rounded-full border-2 border-slate-200 border-t-[#8A6A35] animate-spin" /></div>;

// Each page is its own download; the two busiest are fetched quietly once the first screen is up.
const warm = () => { import('@/pages/Leads'); import('@/pages/Inbox'); };

const Protected = ({ children, adminOnly }) => {
  const { token, ready, user } = useAuth();
  React.useEffect(() => {
    if (!token) return;
    if (window.requestIdleCallback) window.requestIdleCallback(warm, { timeout: 4000 }); else setTimeout(warm, 2000);
  }, [token]);
  if (!ready) return null;
  if (!token) return <Navigate to="/" replace />;
  if (adminOnly && user && (user.role || 'admin') !== 'admin') return <Navigate to="/vc/leads" replace />;
  return <AppShell><React.Suspense fallback={<PageLoading />}>{children}</React.Suspense></AppShell>;
};

// Old bare CRM paths (/leads, /inbox?c=…): staff are forwarded under /vc, everyone else goes to /.
const LegacyRedirect = () => {
  const { token, ready } = useAuth();
  const loc = useLocation();
  if (!ready) return null;
  return <Navigate to={token ? `/vc${loc.pathname}${loc.search}` : '/'} replace />;
};

// Public homepage: shows legal links; signed-in staff get bounced to /leads.
const HomeRoute = () => {
  const { token, ready } = useAuth();
  if (!ready) return null;
  if (token) return <Navigate to="/vc/leads" replace />;
  return <HomePage />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <React.Suspense fallback={null}>
        <Routes>
          <Route path="/vc" element={<LoginPage />} />
          <Route path="/vc/reset-password" element={<ResetPasswordPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
          <Route path="/privacy-policy-bm" element={<PrivacyPolicyPageBM />} />
          <Route path="/terms-of-service" element={<TermsPage />} />
          <Route path="/terms-of-service-bm" element={<TermsOfServicePageBM />} />
          <Route path="/terms" element={<Navigate to="/terms-of-service" replace />} />
          <Route path="/data-deletion" element={<DataDeletionPage />} />
          <Route path="/data-deletion-bm" element={<DataDeletionPageBM />} />
          <Route path="/deletion-status/:code" element={<DeletionStatusPage />} />
          <Route path="/" element={<HomeRoute />} />
          <Route path="/vc/leads" element={<Protected><LeadsPage /></Protected>} />
          <Route path="/vc/inbox" element={<Protected><InboxPage /></Protected>} />
          <Route path="/vc/performance" element={<Protected><PerformancePage /></Protected>} />
          <Route path="/vc/audit" element={<Protected adminOnly><AuditLogPage /></Protected>} />
          <Route path="/vc/team" element={<Protected adminOnly><TeamPage /></Protected>} />
          <Route path="/vc/dashboard" element={<Protected><DashboardPage /></Protected>} />
          <Route path="/vc/import" element={<Protected adminOnly><ImportPage /></Protected>} />
          <Route path="/vc/integrations" element={<Protected><IntegrationsPage /></Protected>} />
          <Route path="/vc/settings" element={<Protected><SettingsPage /></Protected>} />
          <Route path="/vc/campaigns" element={<Protected><CampaignsPage /></Protected>} />
          <Route path="/vc/ads" element={<Protected><AdsPage /></Protected>} />
          <Route path="/vc/forecast" element={<Protected><ForecastPage /></Protected>} />
          <Route path="/vc/duplicates" element={<Protected><DuplicatesPage /></Protected>} />
          <Route path="/vc/automation" element={<Protected><AutomationPage /></Protected>} />
          <Route path="/vc/*" element={<Navigate to="/" replace />} />
          <Route path="/leads" element={<LegacyRedirect />} />
          <Route path="/inbox" element={<LegacyRedirect />} />
          <Route path="/performance" element={<LegacyRedirect />} />
          <Route path="/audit" element={<LegacyRedirect />} />
          <Route path="/team" element={<LegacyRedirect />} />
          <Route path="/dashboard" element={<LegacyRedirect />} />
          <Route path="/import" element={<LegacyRedirect />} />
          <Route path="/integrations" element={<LegacyRedirect />} />
          <Route path="/settings" element={<LegacyRedirect />} />
          <Route path="/campaigns" element={<LegacyRedirect />} />
          <Route path="/ads" element={<LegacyRedirect />} />
          <Route path="/duplicates" element={<LegacyRedirect />} />
          <Route path="/automation" element={<LegacyRedirect />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </React.Suspense>
      </BrowserRouter>
      <Toaster richColors position="bottom-center" />
    </AuthProvider>
  );
}

export default App;
